// components/PayoutDrawer.jsx
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import {
  X,
  Image as ImageIcon,
  CheckCircle2,
  TriangleAlert,
} from "lucide-react";
import { toast } from "react-hot-toast";

const sym = (c) =>
  c === "USD"
    ? "$"
    : c === "EUR"
    ? "€"
    : c === "GBP"
    ? "£"
    : c === "INR"
    ? "₹"
    : c === "LKR"
    ? "Rs"
    : c || "$";
const ts = (v) => v?.toDate?.()?.toLocaleString?.() || "—";

export default function PayoutDrawer({ open, onClose, payoutId, user }) {
  const [payout, setPayout] = useState(null);
  const [agent, setAgent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueText, setIssueText] = useState("");

  useEffect(() => {
    if (!open || !payoutId) return;
    const un = onSnapshot(doc(db, "payouts", payoutId), async (s) => {
      if (!s.exists()) return setPayout(null);
      const p = { id: s.id, ...s.data() };
      setPayout(p);
      // prefetch agent mini-profile
      if (p.agentUid) {
        try {
          const a = await getDoc(doc(db, "users", p.agentUid));
          if (a.exists()) setAgent({ id: a.id, ...a.data() });
          else setAgent(null);
        } catch {
          setAgent(null);
        }
      } else setAgent(null);
    });
    return () => un();
  }, [open, payoutId]);

  const allowConfirm = useMemo(
    () =>
      !!user?.uid &&
      payout?.userUid === user.uid &&
      payout?.paid &&
      !payout?.received,
    [user?.uid, payout?.userUid, payout?.paid, payout?.received]
  );

  const markReceived = async () => {
    if (!allowConfirm) return;
    setBusy(true);
    try {
      await setDoc(
        doc(db, "payouts", payoutId),
        {
          received: true,
          receivedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success("Marked as received");
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Could not update. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const openDispute = async () => {
    if (!user?.uid || payout?.userUid !== user.uid) return;
    setBusy(true);
    try {
      await setDoc(
        doc(db, "payouts", payoutId),
        {
          dispute: {
            isOpen: true,
            reason: issueText || "",
            openedAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast.success("Issue reported");
      setIssueText("");
      setIssueOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Could not report issue.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="absolute right-0 top-0 h-full w-full max-w-md bg-slate-900 p-5 overflow-y-auto border-l border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold">Payout details</div>
          <button
            onClick={onClose}
            className="p-2 rounded bg-slate-800 hover:bg-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        {!payout ? (
          <div className="p-4 rounded-xl bg-slate-800 text-slate-300">
            No payout record yet for this win.
          </div>
        ) : (
          <>
            <div className="space-y-2 text-sm text-slate-300">
              <div className="text-2xl font-extrabold">
                {sym(payout.currency)}
                {Number(payout.amount || 0).toFixed(2)}
              </div>

              <div className="text-xs text-slate-400">
                Ticket: <b>{payout.ticketId}</b> · Card: <b>{payout.cardId}</b>
              </div>

              {/* Agent mini */}
              <div className="mt-2 p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
                <div className="text-xs text-slate-400 mb-1">Agent</div>
                <div className="flex items-center gap-3">
                  <img
                    src={agent?.photoURL || agent?.avatar || "/user.png"}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover ring-1 ring-slate-600"
                  />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">
                      {agent?.displayName ||
                        agent?.username ||
                        payout.agentUid?.slice(0, 6) ||
                        "Agent"}
                    </div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {agent?.email || "—"}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {agent?.phoneE164 || agent?.mobile || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Receipt */}
              <div className="mt-2">
                <div className="text-sm font-semibold mb-1">Receipt</div>
                <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
                  {payout.receiptUrl ? (
                    <a
                      href={payout.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-slate-200 hover:underline"
                    >
                      <ImageIcon size={16} />
                      View receipt image
                    </a>
                  ) : (
                    <div className="text-slate-400 text-sm">
                      No receipt uploaded (may have been cash).
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
                  <div className="text-xs text-slate-400">Paid</div>
                  <div className="font-semibold">
                    {payout.paid ? "Yes" : "No"}{" "}
                    <span className="text-xs text-slate-400 ml-1">
                      {payout.paid ? ts(payout.paidAt) : ""}
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
                  <div className="text-xs text-slate-400">Received (you)</div>
                  <div className="font-semibold">
                    {payout.received ? "Yes" : "No"}{" "}
                    <span className="text-xs text-slate-400 ml-1">
                      {payout.received ? ts(payout.receivedAt) : ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Primary action */}
              <div className="mt-3 flex items-center gap-2">
                {allowConfirm ? (
                  <button
                    onClick={markReceived}
                    disabled={busy}
                    className="px-4 py-2 rounded-xl bg-emerald-400 text-black font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    <CheckCircle2 size={18} />
                    Mark as received
                  </button>
                ) : (
                  <div className="text-xs text-slate-400">
                    {payout.paid
                      ? payout.received
                        ? "You already confirmed."
                        : "Waiting for agent or not your payout."
                      : "Agent hasn’t marked this as paid yet."}
                  </div>
                )}

                <button
                  onClick={() => setIssueOpen((v) => !v)}
                  className="px-3 py-2 rounded-xl bg-slate-800 ring-1 ring-slate-700 text-white text-sm inline-flex items-center gap-2"
                >
                  <TriangleAlert size={16} />
                  Report an issue
                </button>
              </div>

              {issueOpen && (
                <div className="mt-2">
                  <textarea
                    className="w-full min-h-[80px] p-3 rounded-xl bg-slate-800 text-white ring-1 ring-slate-700"
                    placeholder="Describe the issue (optional)"
                    value={issueText}
                    onChange={(e) => setIssueText(e.target.value)}
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={openDispute}
                      disabled={busy}
                      className="px-3 py-2 rounded-xl bg-amber-400 text-black font-semibold disabled:opacity-60"
                    >
                      Submit
                    </button>
                    <button
                      onClick={() => {
                        setIssueText("");
                        setIssueOpen(false);
                      }}
                      className="px-3 py-2 rounded-xl bg-slate-700 text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
